/** Visibilité propre au document logique, indépendante de son montage. */
export type CanvasSessionVisibility = 'visible' | 'masked';

/** Présence ou absence d'un éditeur vivant pour cette session. */
export type CanvasSessionTemperature = 'hot' | 'cold' | 'hydrating';

export type CanvasSessionActivityKind = 'stream' | 'writer' | 'mutation';

/** Une session épinglée est retenue par un travail documentaire. */
export type CanvasSessionPinning = 'pinned' | 'free';

/** État du checkpoint durable du document logique. */
export type CanvasSessionCheckpoint = 'clean' | 'dirty' | 'checkpointing' | 'error';

/**
 * Époque d'une session ACQUISE mais jamais montée.
 *
 * ⚠️ **C'est une valeur sentinelle, et elle est nommée pour cesser d'être
 * implicite** (#1336). Tant que `documentSessionId` portait l'identité d'un
 * document, `mountEpoch: 0` n'était qu'un compteur au repos. Depuis que
 * `(conversationId, mountEpoch)` EST l'identité d'un montage, l'époque `0` ne
 * désigne aucun montage : elle vaut pour toutes les sessions jamais montées de
 * l'onglet, et deux instants « avant montage » d'une même conversation y sont
 * indiscernables.
 *
 * Conséquence appliquée, pas commentée : {@link CanvasSessionRegistry.decideCallback}
 * refuse toute identité portant cette valeur (`mount-not-started`). Un geste
 * capturé avant le premier montage ne peut donc jamais être rapproché d'un
 * montage réel.
 */
export const EPOQUE_AVANT_MONTAGE = 0;

/**
 * Projection publique immutable d'une session documentaire.
 *
 * ⚠️ **`(conversationId, mountEpoch)` est l'identité** (#1336). Il n'existe plus
 * de `documentSessionId` : c'était un UUID frappé par le registre, dont
 * l'unicité par conversation était déjà appliquée par l'adoption — donc un
 * second nom pour un fait que la conversation portait déjà. `conversationId`
 * est la clé du document logique ; l'époque distingue ses montages successifs.
 */
export interface CanvasSessionSnapshot {
  readonly conversationId: string;
  readonly mountEpoch: number;
  readonly visibility: CanvasSessionVisibility;
  readonly temperature: CanvasSessionTemperature;
  readonly pinning: CanvasSessionPinning;
  readonly checkpoint: CanvasSessionCheckpoint;
  readonly lastUsedSequence: number;
  readonly pinCount: number;
  readonly streamCount: number;
  readonly writerCount: number;
  readonly mutationCount: number;
}

/** Collection observable consommée par la résidence, sans destination implicite. */
export interface CanvasSessionRegistrySnapshot {
  readonly visibleConversationId: string | null;
  readonly sessions: readonly CanvasSessionSnapshot[];
}

/** One exact resident must not be replaced or cooled while any work owns it. */
export function isCanvasSessionProtected(
  session: Pick<CanvasSessionSnapshot, 'pinCount' | 'streamCount' | 'writerCount' | 'mutationCount'> | null,
): boolean {
  return Boolean(
    session
    && (
      session.pinCount > 0
      || session.streamCount > 0
      || session.writerCount > 0
      || session.mutationCount > 0
    )
  );
}

export type CanvasSessionTransition =
  | { readonly axis: 'visibility'; readonly value: CanvasSessionVisibility }
  | { readonly axis: 'temperature'; readonly value: CanvasSessionTemperature }
  | { readonly axis: 'pinning'; readonly value: CanvasSessionPinning }
  | { readonly axis: 'checkpoint'; readonly value: CanvasSessionCheckpoint };

/** Identité complète capturée par un callback documentaire asynchrone. */
export interface CanvasSessionCallbackIdentity {
  readonly conversationId: string;
  readonly mountEpoch: number;
}

/**
 * Pourquoi un geste asynchrone ne retrouve pas le montage qu'il a capturé.
 *
 * ⚠️ **Trois issues, et `conversation-mismatch` n'en est plus une** (#1336).
 * Elle comparait le propriétaire de la session trouvée par `documentSessionId`
 * à celui qu'annonçait le callback ; la conversation étant désormais la CLÉ de
 * la collection, cette comparaison est une tautologie — un capteur qui rendrait
 * la même valeur avec le défaut et sans lui. La retirer n'efface aucune cause :
 * les trois causes réelles restent nommées séparément, dont la nouvelle
 * `mount-not-started`, que l'ancien modèle repliait silencieusement sur un
 * ACCORD (deux gestes capturés avant montage portaient tous deux l'époque 0).
 */
export type CanvasCallbackDecision =
  | {
      readonly accepted: true;
      readonly session: CanvasSessionSnapshot;
    }
  | {
      readonly accepted: false;
      readonly reason: 'session-not-found' | 'mount-not-started' | 'mount-epoch-mismatch';
    };

function cloneSession(session: CanvasSessionSnapshot): CanvasSessionSnapshot {
  return { ...session };
}

/**
 * Registre en mémoire des documents logiques ouverts pendant la durée de l'onglet.
 *
 * Il ne contient aucune ressource SuperDoc : celles-ci vivent dans les scopes du
 * `CanvasDocumentScopeDirectory`. Le registre ne porte que l'identité et les axes
 * qui survivent aux montages et aux refroidissements.
 *
 * ⚠️ **Le registre ne FRAPPE plus aucune identité** (#1336). Il n'y a plus de
 * `crypto.randomUUID`, plus de fabrique d'identifiants, et plus de pierres
 * tombales `issuedDocumentSessionIds` : le document d'une conversation est
 * désigné par cette conversation, une valeur que le registre reçoit au lieu de
 * l'inventer. Ce que garantissaient les tombstones — « un callback de l'ancien
 * compte ne peut pas être confondu avec une session du nouveau » — est
 * entièrement porté par `nextMountEpoch`, compteur monotone d'onglet jamais
 * remis à zéro, purge comprise. C'est la seule chose qui départage deux
 * montages successifs d'une même conversation, et c'est pourquoi elle ne doit
 * jamais être réinitialisée.
 *
 * ⚠️ **Il n'y a plus d'adaptateur mono-session au-dessus** (#1334). La façade
 * `CanvasSingleSessionAdapter` portait deux états qui étaient chacun le MIROIR
 * d'un fait déjà écrit ici, et un miroir se désynchronise :
 * - une map `conversationId → documentSessionId`, alors que la session PORTE son
 *   `conversationId` ; les deux étaient écrits dans la même transaction et rien
 *   ne le garantissait. Depuis #1336 la question ne se pose plus : la
 *   conversation est la clé, il n'y a plus rien à faire correspondre.
 * - un `currentDocumentSessionId` doublant la projection visible, dont il
 *   divergeait au premier masquage : masquer laissait le miroir désigner un
 *   document que plus rien n'affichait, et c'est lui que lisaient le repli des
 *   attentes sans identité et la « session courante ».
 */
export class CanvasSessionRegistry {
  /** Clé = `conversationId`. Une conversation possède au plus un document. */
  private readonly sessions = new Map<string, CanvasSessionSnapshot>();
  private readonly listeners = new Set<(snapshot: CanvasSessionRegistrySnapshot) => void>();
  private visibleConversationId: string | null = null;
  /**
   * Allocation monotone à l'échelle de l'onglet, même entre deux documents.
   * WHY : l'époque est la moitié de l'identité d'un montage ; recommencer à `1`
   * sur la session suivante rendrait un callback ancien indiscernable du neuf.
   */
  private nextMountEpoch = EPOQUE_AVANT_MONTAGE;
  /** Ordinal global : une horloge murale rendrait les égalités LRU ambiguës. */
  private nextUseSequence = 0;
  private readonly pinTokens = new Map<string, Set<string>>();
  private readonly activityTokens = new Map<string, Map<CanvasSessionActivityKind, Set<string>>>();

  getSnapshot(): CanvasSessionRegistrySnapshot {
    return {
      visibleConversationId: this.visibleConversationId,
      sessions: [...this.sessions.values()].map(cloneSession),
    };
  }

  subscribe(listener: (snapshot: CanvasSessionRegistrySnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  selectSession(conversationId: string): CanvasSessionSnapshot | null {
    return this.transitionSession(conversationId, { axis: 'visibility', value: 'visible' });
  }

  maskSession(conversationId: string): CanvasSessionSnapshot | null {
    return this.transitionSession(conversationId, { axis: 'visibility', value: 'masked' });
  }

  getSession(conversationId: string): CanvasSessionSnapshot | null {
    const session = this.sessions.get(conversationId);
    return session ? cloneSession(session) : null;
  }

  /**
   * Retire le document d'UNE conversation, qu'il soit ou non la projection visible.
   *
   * Le fil supprimé par l'avocat peut très bien être parqué derrière un autre
   * document (#1334) : c'est pourquoi la cible est nommée et non déduite de la
   * sélection. La projection visible n'est libérée que si c'est précisément
   * cette conversation qui l'occupait.
   *
   * ⚠️ Rien n'interdit à cette conversation de racquérir un document ensuite —
   * c'est le geste « Nouveau ». Ce qui interdit à un callback de l'ancien
   * document de toucher le nouveau, c'est l'époque : le compteur d'onglet
   * n'étant jamais remis à zéro, le montage suivant en portera une strictement
   * postérieure.
   */
  deleteSession(conversationId: string): boolean {
    const deleted = this.sessions.delete(conversationId);
    if (!deleted) return false;
    if (this.visibleConversationId === conversationId) {
      this.visibleConversationId = null;
    }
    this.pinTokens.delete(conversationId);
    this.activityTokens.delete(conversationId);
    this.notify();
    return true;
  }

  /**
   * Réserve le document d'une conversation, ou rend celui qu'elle possède déjà.
   *
   * ⚠️ **Seul point d'entrée qui crée une session, et il exige un
   * propriétaire** (#1336). L'ancienne transaction en deux temps —
   * `createSession()` frappait une identité sans propriétaire, `adoptSession`
   * la lui donnait juste après — n'a plus lieu d'être : il n'y a plus
   * d'identité à frapper avant de connaître la conversation. La propriété
   * « aucune session sans propriétaire n'est jamais publiée », que #1334 avait
   * dû obtenir en retirant un `notify()`, est désormais STRUCTURELLE — il
   * n'existe aucun état intermédiaire à publier.
   *
   * ⚠️ **Un seul verbe d'acquisition** (#1334). L'adaptateur en exposait
   * deux — `acquireForConversation` et `acquireManualForConversation` — dont les
   * corps étaient littéralement identiques : elles laissaient croire que le
   * geste agent et le geste humain réservaient différemment, alors que la
   * distinction vit ailleurs (l'épinglage de tour, que seul l'agent prend).
   *
   * Cette réservation ne touche PAS à la projection visible : deux conversations
   * peuvent posséder leur document logique alors qu'un seul host est à l'écran.
   */
  acquerirPourConversation(conversationId: string): CanvasSessionSnapshot {
    if (conversationId.trim().length === 0) {
      throw new Error(
        'Canvas session acquisition refused: conversationId is empty. '
        + 'The conversation identifies the logical document since #1336; '
        + 'resolve a confirmed conversation before acquiring its canvas session.',
      );
    }
    const existing = this.sessions.get(conversationId);
    if (existing) return cloneSession(existing);

    const session: CanvasSessionSnapshot = {
      conversationId,
      mountEpoch: EPOQUE_AVANT_MONTAGE,
      visibility: 'masked',
      temperature: 'cold',
      pinning: 'free',
      checkpoint: 'clean',
      lastUsedSequence: 0,
      pinCount: 0,
      streamCount: 0,
      writerCount: 0,
      mutationCount: 0,
    };
    this.sessions.set(conversationId, session);
    this.notify();
    return cloneSession(session);
  }

  transitionSession(
    conversationId: string,
    transition: CanvasSessionTransition,
  ): CanvasSessionSnapshot | null {
    const session = this.sessions.get(conversationId);
    if (!session) return null;

    if (transition.axis === 'visibility') {
      if (session.visibility === transition.value) return cloneSession(session);
      if (transition.value === 'visible') {
        const previouslyVisibleId = this.visibleConversationId;
        if (previouslyVisibleId && previouslyVisibleId !== conversationId) {
          const previouslyVisible = this.sessions.get(previouslyVisibleId);
          if (previouslyVisible) {
            this.sessions.set(previouslyVisibleId, { ...previouslyVisible, visibility: 'masked' });
          }
        }
        this.visibleConversationId = conversationId;
      } else if (this.visibleConversationId === conversationId) {
        this.visibleConversationId = null;
      }
      const updated = { ...session, visibility: transition.value };
      this.sessions.set(conversationId, updated);
      this.notify();
      return cloneSession(updated);
    }

    if (transition.axis === 'temperature') {
      if (session.temperature === transition.value) return cloneSession(session);
      const updated = { ...session, temperature: transition.value };
      this.sessions.set(conversationId, updated);
      this.notify();
      return cloneSession(updated);
    }

    if (transition.axis === 'pinning') {
      const tokens = this.pinTokens.get(conversationId) ?? new Set<string>();
      if (transition.value === 'pinned') tokens.add('legacy');
      else tokens.delete('legacy');
      if (tokens.size > 0) this.pinTokens.set(conversationId, tokens);
      else this.pinTokens.delete(conversationId);
      const pinning: CanvasSessionPinning = tokens.size > 0 ? 'pinned' : 'free';
      if (session.pinning === pinning && session.pinCount === tokens.size) return cloneSession(session);
      const updated = { ...session, pinning, pinCount: tokens.size };
      this.sessions.set(conversationId, updated);
      this.notify();
      return cloneSession(updated);
    }

    if (session.checkpoint === transition.value) return cloneSession(session);
    const updated = { ...session, checkpoint: transition.value };
    this.sessions.set(conversationId, updated);
    this.notify();
    return cloneSession(updated);
  }

  advanceMountEpoch(conversationId: string): CanvasSessionSnapshot | null {
    const session = this.sessions.get(conversationId);
    if (!session) return null;
    this.nextMountEpoch += 1;
    const mounted = { ...session, mountEpoch: this.nextMountEpoch };
    this.sessions.set(conversationId, mounted);
    this.notify();
    return cloneSession(mounted);
  }

  touchSession(conversationId: string): CanvasSessionSnapshot | null {
    const session = this.sessions.get(conversationId);
    if (!session) return null;
    this.nextUseSequence += 1;
    const updated = { ...session, lastUsedSequence: this.nextUseSequence };
    this.sessions.set(conversationId, updated);
    this.notify();
    return cloneSession(updated);
  }

  pinSession(conversationId: string, token: string): (() => void) | null {
    const session = this.sessions.get(conversationId);
    if (!session) return null;
    const tokens = this.pinTokens.get(conversationId) ?? new Set<string>();
    if (!tokens.has(token)) tokens.add(token);
    this.pinTokens.set(conversationId, tokens);
    this.sessions.set(conversationId, { ...session, pinning: 'pinned', pinCount: tokens.size });
    this.notify();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const liveTokens = this.pinTokens.get(conversationId);
      if (!liveTokens?.delete(token)) return;
      if (liveTokens.size === 0) this.pinTokens.delete(conversationId);
      const live = this.sessions.get(conversationId);
      if (!live) return;
      this.sessions.set(conversationId, {
        ...live,
        pinning: liveTokens.size > 0 ? 'pinned' : 'free',
        pinCount: liveTokens.size,
      });
      this.notify();
    };
  }

  beginActivity(
    conversationId: string,
    kind: CanvasSessionActivityKind,
    token: string,
  ): (() => void) | null {
    const session = this.sessions.get(conversationId);
    if (!session) return null;
    const byKind = this.activityTokens.get(conversationId) ?? new Map();
    const tokens = byKind.get(kind) ?? new Set<string>();
    tokens.add(token);
    byKind.set(kind, tokens);
    this.activityTokens.set(conversationId, byKind);
    this.sessions.set(conversationId, this.withActivityCount(session, kind, tokens.size));
    this.notify();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const liveByKind = this.activityTokens.get(conversationId);
      const liveTokens = liveByKind?.get(kind);
      if (!liveTokens?.delete(token)) return;
      if (liveTokens.size === 0) liveByKind?.delete(kind);
      if (liveByKind?.size === 0) this.activityTokens.delete(conversationId);
      const live = this.sessions.get(conversationId);
      if (!live) return;
      this.sessions.set(conversationId, this.withActivityCount(live, kind, liveTokens.size));
      this.notify();
    };
  }

  /**
   * Ce geste asynchrone porte-t-il encore le montage qu'il a capturé ?
   *
   * Garde canonique du registre : toute reprise de travail après un `await`
   * passe par ici. Les trois refus sont nommés séparément parce qu'ils
   * n'appellent pas la même suite — document disparu, geste antérieur à tout
   * montage, montage remplacé.
   */
  decideCallback(identity: CanvasSessionCallbackIdentity): CanvasCallbackDecision {
    const session = this.sessions.get(identity.conversationId);
    if (!session) {
      return { accepted: false, reason: 'session-not-found' };
    }
    // Avant la comparaison d'époques, et pas après : `0 === 0` accepterait
    // sinon un geste capturé avant le premier montage, sur une session que rien
    // n'a encore montée. L'époque nulle ne désigne aucun montage — voir
    // EPOQUE_AVANT_MONTAGE.
    if (identity.mountEpoch === EPOQUE_AVANT_MONTAGE) {
      return { accepted: false, reason: 'mount-not-started' };
    }
    if (session.mountEpoch !== identity.mountEpoch) {
      return { accepted: false, reason: 'mount-epoch-mismatch' };
    }
    return { accepted: true, session: cloneSession(session) };
  }

  /**
   * Sélectionne le host visible, et rafraîchit son rang LRU au passage.
   *
   * Le `touch` n'est fait que sur un CHANGEMENT de sélection : resélectionner
   * le document déjà à l'écran n'est pas un usage, et le compter ferait remonter
   * un résident au sommet du LRU sans que l'avocat ait rien fait.
   */
  selectVisibleSession(conversationId: string): CanvasSessionSnapshot | null {
    const alreadyVisible = this.visibleConversationId === conversationId;
    const selected = this.selectSession(conversationId);
    if (!selected) return null;
    return alreadyVisible ? selected : this.touchSession(conversationId) ?? selected;
  }

  /**
   * Avance une époque seulement si le geste porte encore le montage qu'il a
   * capturé. Une réponse de téléchargement tardive ne peut ainsi jamais
   * remonter le document réouvert entre-temps.
   */
  remountExactSession(identity: CanvasSessionCallbackIdentity): CanvasCallbackDecision {
    const decision = this.decideCallback(identity);
    if (!decision.accepted) return decision;
    const mounted = this.advanceMountEpoch(identity.conversationId);
    return mounted
      ? { accepted: true, session: mounted }
      : { accepted: false, reason: 'session-not-found' };
  }

  /** Ouvre une époque pour un document froid dont la conversation n'a pas bougé. */
  mountColdSession(conversationId: string): CanvasSessionSnapshot | null {
    if (!this.sessions.has(conversationId)) return null;
    return this.advanceMountEpoch(conversationId);
  }

  /** Un montage devenu prêt est chaud, et vient d'être utilisé. */
  markMountHot(identity: CanvasSessionCallbackIdentity): CanvasSessionSnapshot | null {
    const decision = this.decideCallback(identity);
    if (!decision.accepted) return null;
    const hot = this.transitionSession(identity.conversationId, {
      axis: 'temperature',
      value: 'hot',
    });
    return hot ? this.touchSession(identity.conversationId) ?? hot : null;
  }

  purgeSessions(): number {
    const removedCount = this.sessions.size;
    this.sessions.clear();
    this.pinTokens.clear();
    this.activityTokens.clear();
    this.visibleConversationId = null;
    // Ne JAMAIS remettre `nextMountEpoch` à zéro : c'est le seul discriminant
    // entre deux montages d'une même conversation, et le nouveau compte peut
    // rouvrir exactement les mêmes fils que l'ancien. Un callback parti sous le
    // compte précédent doit rester impossible à confondre avec un montage du
    // nouveau.
    if (removedCount > 0) this.notify();
    return removedCount;
  }

  private notify(): void {
    const snapshot = this.getSnapshot();
    for (const listener of [...this.listeners]) {
      try { listener(snapshot); } catch { /* one resident must not block the others */ }
    }
  }

  private withActivityCount(
    session: CanvasSessionSnapshot,
    kind: CanvasSessionActivityKind,
    count: number,
  ): CanvasSessionSnapshot {
    if (kind === 'stream') return { ...session, streamCount: count };
    if (kind === 'writer') return { ...session, writerCount: count };
    return { ...session, mutationCount: count };
  }
}

/** Registre mémoire partagé par la résidence canvas pendant la durée de l'onglet. */
export const canvasSessionRegistry = new CanvasSessionRegistry();
